import React, { useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/common/dialog"
import { Button } from "@components/common/button"
import { Info, PlusCircle } from "lucide-react"
import { Label } from "@components/common/label"
import { Input } from "@components/common/input"
import useTranslator from "@pma/hooks/use-translator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { DictType } from "@pma/types"
import { toast } from "@pma/hooks/use-toast"
import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import { ADD_CUSTOM_COLUMNS_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { cn } from "@pma/utils/helpers"
import { useNavigate, useSearchParams } from "react-router-dom"
import { RadioGroup, RadioGroupItem } from "../common/radio-group"
import { useCustomColumns } from "@pma/stores/tasks-store"

interface Props {
  context: string
  projectId: string
}

const columnTypesOptions = ["Integer", "Decimal", "Text", "Boolean", "Checkbox", "Date", "Timestamp", "Range", "Selector", "File"]

const AddTaskTableColumn = ({ context, projectId }: Props) => {
  const [newColumnName, setNewColumnName] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [columnType, setColumnType] = useState<string>("Text")

  const { translate, locale } = useTranslator()
  const { setCustomColumns } = useCustomColumns()

  const navigate = useNavigate()

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const isHebrew = locale === "he"

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const addProjectCustomColumn = async () => {
    const response = await axios.post(
      SERVER_URL + ADD_CUSTOM_COLUMNS_ENDPOINT,
      {
        project_id: projectId,
        name: newColumnName,
        type: columnType.toLocaleLowerCase(),
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const addProjectCustomColumnMutation = useMutation({
    mutationKey: ["add-project-custom-column"],
    mutationFn: addProjectCustomColumn,
    onSuccess: (data) => {
      setNewColumnName("")
      setColumnType("Text")
      setDialogOpen(false)
      toast({
        title: translate("Success"),
        description: translate("Column added to the project task table"),
        variant: "success",
      })

      setCustomColumns(data?._custom_columns)

      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to add column"),
        variant: "error",
      })
    },
  })
  // Add a new column
  const addColumn = () => {
    if (!newColumnName.trim()) {
      return
    }

    addProjectCustomColumnMutation.mutate()
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <Tooltip>
        <TooltipTrigger className='mx-2'>
          <Info size={14} />
        </TooltipTrigger>
        <TooltipContent>{translate(`Add new columns to the ${context} table`)}</TooltipContent>
      </Tooltip>
      <DialogTrigger asChild className={isHebrew ? "rtl" : "ltr"}>
        <Button size={"sm"} variant={"outline"} className='flex items-center justify-start'>
          <PlusCircle className='mx-2 h-4 w-4' />
          {translate("Add Column")}{" "}
        </Button>
      </DialogTrigger>
      <DialogContent className={cn("z-50", isHebrew ? "rtl" : "ltr")}>
        <DialogHeader>
          <DialogTitle className={isHebrew ? "text-right" : "text-left"}>{translate("Add New Column")}</DialogTitle>
        </DialogHeader>
        <div className='flex w-full'>
          <div className='w-full'>
            <Label htmlFor='column-name'>{translate("Column Name")}</Label>
            <div className='flex w-full'>
              <Input
                id='column-name'
                className={cn("w-full flex-1")}
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                placeholder={translate("Enter column name")}
              />
            </div>
            <div className='w-full py-2'>
              <Label htmlFor='column-type'>{translate("Column Type")}</Label>
              <div className='flex w-full'>
                <RadioGroup defaultValue='Text' className='flex flex-wrap gap-x-4 gap-y-2'>
                  {columnTypesOptions.map((option, index: number) => (
                    <div className='flex items-center space-x-2' key={index}>
                      <RadioGroupItem
                        value={option}
                        id={option}
                        onClick={() => setColumnType(option)}
                      />
                      <Label htmlFor={option}>{translate(option)}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>
        </div>
        <Button
          onClick={addColumn}
          loading={addProjectCustomColumnMutation.isPending}
          disabled={addProjectCustomColumnMutation.isPending || !newColumnName}
        >
          {translate("Add Column")}
        </Button>
      </DialogContent>
    </Dialog>
  )
}

export default AddTaskTableColumn
